import { Close, History, Terrain } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MapContainer,
  useMap,
  ZoomControl,
} from 'react-leaflet';

import type { FieldSummary } from '@/api/fields';
import { fieldsApi } from '@/api/fields';
import {
  FIELD_MARKER_ZOOM,
  MENA_DEFAULT_ZOOM,
  MENA_MAP_CENTER,
} from '@/constants/fieldMap';
import type { LatLng } from '@/utils/fieldCoordinate';
import { parsePointCoordinate } from '@/utils/fieldCoordinate';

import { FieldMarkerCluster, type FieldClusterPoint } from './FieldMarkerCluster';
import { MapBaseLayers } from './mapLayers';

type FieldMapPoint = FieldClusterPoint<FieldSummary>;

function MapFitBounds({ positions }: { positions: LatLng[] }) {
  const map = useMap();

  useEffect(() => {
    if (positions.length === 0) {
      return;
    }
    if (positions.length === 1) {
      map.setView([positions[0].lat, positions[0].lng], FIELD_MARKER_ZOOM, { animate: false });
      return;
    }
    const bounds = L.latLngBounds(positions.map((p) => [p.lat, p.lng] as [number, number]));
    map.fitBounds(bounds, { padding: [48, 48], maxZoom: 10 });
  }, [map, positions]);

  return null;
}

interface FieldsMapViewProps {
  mapHeight?: number | string;
}

export const FieldsMapView: React.FC<FieldsMapViewProps> = ({ mapHeight = 560 }) => {
  const navigate = useNavigate();
  const [mapReady, setMapReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<FieldSummary[]>([]);
  const [selectedField, setSelectedField] = useState<FieldMapPoint | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setMapReady(true), 50);
    return () => {
      window.clearTimeout(timer);
      setMapReady(false);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadFields = async () => {
      try {
        setLoading(true);
        setError(null);
        // Catalogue fields only — scenario.coordinate pins must never appear here.
        const response = await fieldsApi.getFieldsList();
        if (!cancelled) {
          setFields(response.fields);
        }
      } catch (err) {
        console.error('Failed to load fields for map:', err);
        if (!cancelled) {
          setError('Failed to load fields');
          setFields([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadFields();
    return () => {
      cancelled = true;
    };
  }, []);

  const mapPoints = useMemo<FieldMapPoint[]>(() => {
    return fields.flatMap((field) => {
      const position = parsePointCoordinate(field.coordinate);
      if (!position) {
        return [];
      }
      return [{ id: field.id, position, data: field }];
    });
  }, [fields]);

  const mapCenter = useMemo<[number, number]>(() => {
    if (mapPoints.length > 0) {
      return [mapPoints[0].position.lat, mapPoints[0].position.lng];
    }
    return [MENA_MAP_CENTER.lat, MENA_MAP_CENTER.lng];
  }, [mapPoints]);

  const initialZoom = mapPoints.length > 0 ? FIELD_MARKER_ZOOM : MENA_DEFAULT_ZOOM;
  const positions = useMemo(() => mapPoints.map((p) => p.position), [mapPoints]);

  const handleSelectPoint = useCallback((point: FieldMapPoint) => {
    setSelectedField(point);
  }, []);

  const handleOpenHistory = (fieldId: string) => {
    navigate(`/app/history?fieldId=${fieldId}`);
  };

  return (
    <Box>
      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Loading fields...
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && mapPoints.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No fields with coordinates are available yet.
        </Alert>
      )}

      <Box
        sx={{
          position: 'relative',
          height: mapHeight,
          borderRadius: 1,
          overflow: 'hidden',
          border: 1,
          borderColor: 'divider',
          '& .leaflet-container': { height: '100%', width: '100%', zIndex: 0 },
          '& .leaflet-control-layers': { fontSize: '12px' },
        }}
      >
        {mapReady ? (
          <MapContainer
            center={mapCenter}
            zoom={initialZoom}
            minZoom={2}
            maxZoom={14}
            scrollWheelZoom
            style={{ height: '100%', width: '100%' }}
            zoomControl={false}
          >
            <MapBaseLayers />
            <ZoomControl position="bottomright" />
            <MapFitBounds positions={positions} />
            <FieldMarkerCluster points={mapPoints} onSelect={handleSelectPoint} />
          </MapContainer>
        ) : (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Loading map...
            </Typography>
          </Box>
        )}

        {selectedField && (
          <Card
            elevation={4}
            sx={{
              position: 'absolute',
              left: 16,
              bottom: 16,
              zIndex: 1000,
              width: { xs: 'calc(100% - 32px)', sm: 320 },
              maxWidth: 360,
            }}
          >
            <CardContent sx={{ pb: '16px !important' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Terrain color="primary" sx={{ mt: 0.25 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {selectedField.data.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {selectedField.data.type}
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<History />}
                    onClick={() => handleOpenHistory(selectedField.data.id)}
                  >
                    Open historical data
                  </Button>
                </Box>
                <IconButton
                  size="small"
                  aria-label="Close"
                  onClick={() => setSelectedField(null)}
                  sx={{ mt: -0.5, mr: -0.5 }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>

      {!loading && mapPoints.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {mapPoints.length} field{mapPoints.length === 1 ? '' : 's'} on map.
          {' '}Clusters show counts until zoom 10; click a marker for details.
        </Typography>
      )}
    </Box>
  );
};
